import { useState } from "react";
import SectionHeading from "@/components/feature/SectionHeading";
import { latestCards, type LatestCard } from "@/mocks/home";

const tabFilters = [
  { label: "全部", tags: null as string[] | null },
  { label: "今天刚出", tags: ["今天刚出", "新出"] },
  { label: "热传中", tags: ["热传中", "正在刷屏"] },
  { label: "刚补档", tags: ["刚补档", "更新"] },
];

function CardBadge({ badge }: { badge: string }) {
  return (
    <span className="absolute left-3 top-3 flex h-6 min-w-6 items-center justify-center rounded-full bg-primary-500 px-2 text-xs font-medium text-background-50">
      {badge}
    </span>
  );
}

function FeaturedCard({ card }: { card: LatestCard }) {
  return (
    <article className="group grid overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300 lg:grid-cols-2">
      <div className="relative h-56 w-full overflow-hidden lg:h-full lg:min-h-[320px]">
        <img
          src={card.image}
          alt={`黑料视频 ${card.title}`}
          title={`${card.title} 最新热传`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <CardBadge badge={card.badge} />
        <span className="absolute bottom-3 left-3 rounded-full bg-black/55 px-2.5 py-1 text-xs text-background-100 backdrop-blur-sm">
          {card.tag}
        </span>
      </div>
      <div className="flex flex-col justify-center p-5 md:p-8">
        <div className="flex items-center gap-2 text-xs text-foreground-500">
          <span className="flex items-center gap-1 whitespace-nowrap">
            <i className="ri-time-line"></i>
            {card.time}
          </span>
        </div>
        <h3 className="mt-3 font-heading text-xl font-bold leading-snug text-foreground-950 md:text-2xl">
          {card.title}
        </h3>
        <p className="mt-3 text-sm leading-relaxed text-foreground-600">{card.summary}</p>
        <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-500">
          <span className="flex items-center gap-1 whitespace-nowrap">
            <i className="ri-eye-line text-sm"></i>
            {card.views} 次浏览
          </span>
          <span className="flex items-center gap-1 whitespace-nowrap">
            <i className="ri-chat-3-line text-sm"></i>
            {card.comments} 条现场评论
          </span>
          <span className="flex items-center gap-1 whitespace-nowrap text-primary-600">
            <i className="ri-arrow-right-line text-sm"></i>
            回看更新
          </span>
        </div>
      </div>
    </article>
  );
}

function GridCard({ card }: { card: LatestCard }) {
  return (
    <article className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300">
      <div className="relative h-44 w-full overflow-hidden">
        <img
          src={card.image}
          alt={`黑料视频 ${card.title}`}
          title={`${card.title} 最新热传`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <CardBadge badge={card.badge} />
      </div>
      <div className="flex flex-1 flex-col p-5">
        <div className="flex items-center gap-2 text-xs">
          <span className="whitespace-nowrap rounded-full bg-secondary-100 px-2 py-0.5 font-medium text-secondary-900">
            {card.tag}
          </span>
          <span className="text-foreground-400">{card.time}</span>
        </div>
        <h3 className="mt-3 font-heading text-base font-semibold leading-snug text-foreground-950 transition-colors line-clamp-2 group-hover:text-primary-600">
          {card.title}
        </h3>
        <p className="mt-2 text-xs leading-relaxed text-foreground-600 line-clamp-2">
          {card.summary}
        </p>
        <div className="mt-auto flex items-center gap-4 pt-4 text-xs text-foreground-500">
          <span className="flex items-center gap-1 whitespace-nowrap">
            <i className="ri-eye-line text-sm"></i>
            {card.views}
          </span>
          <span className="flex items-center gap-1 whitespace-nowrap">
            <i className="ri-chat-3-line text-sm"></i>
            {card.comments}
          </span>
        </div>
      </div>
    </article>
  );
}

export default function LatestFlow() {
  const [activeTab, setActiveTab] = useState("全部");

  const activeFilter = tabFilters.find((tab) => tab.label === activeTab);
  const filtered = activeFilter?.tags
    ? latestCards.filter((card) => activeFilter.tags?.includes(card.tag))
    : latestCards;

  const featured = filtered[0];
  const rest = filtered.slice(1);

  return (
    <section id="latest" className="w-full bg-background-50">
      <div className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
        <SectionHeading
          eyebrow="今天刚出的都在这"
          title="最新更新 · 大图卡片流"
          description="按更新时间往下排，标题优先、配图克制，先看标题挑中的再点开回看，不用在信息流里反复翻。"
          action={
            <div className="inline-flex flex-wrap items-center gap-1 rounded-full border border-background-200 bg-background-100 p-1">
              {tabFilters.map((tab) => (
                <button
                  key={tab.label}
                  type="button"
                  onClick={() => setActiveTab(tab.label)}
                  className={
                    tab.label === activeTab
                      ? "cursor-pointer whitespace-nowrap rounded-full bg-primary-500 px-4 py-1.5 text-sm font-medium text-background-50"
                      : "cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium text-foreground-600 transition-colors hover:bg-background-200"
                  }
                >
                  {tab.label}
                </button>
              ))}
            </div>
          }
        />

        {featured ? (
          <div className="mt-8 md:mt-10">
            <FeaturedCard card={featured} />
          </div>
        ) : (
          <div className="mt-8 rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-12 text-center text-sm text-foreground-500">
            这一类暂时没有新出的更新，换个分类看看。
          </div>
        )}

        {rest.length > 0 && (
          <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {rest.map((card) => (
              <GridCard key={card.id} card={card} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}